import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Avatar,
  Chip,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  AttachMoney as MoneyIcon,
  FamilyRestroom as FamilyIcon,
  LocationOn as LocationIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useLocation } from '../../../contexts/LocationContext';
import api from '../../../utils/apiConfig';

const PersonaManagement = () => {
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const { selectedLocation } = useLocation();
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ currentPage: 1, itemsPerPage: 12, totalItems: 0, totalPages: 0 });
  const [error, setError] = useState(null);

  // Load personas from API
  useEffect(() => {
    const fetchPersonas = async () => {
      if (!selectedLocation?._id) return;
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get('/marketing/personas', {
          params: {
            locationId: selectedLocation._id,
            page: pagination.currentPage,
            limit: pagination.itemsPerPage,
          },
        });
        const personasData = data?.data?.personas || [];
        const pg = data?.data?.pagination || {};
        setPersonas(personasData);
        setPagination((prev) => ({
          ...prev,
          currentPage: pg.currentPage || prev.currentPage,
          itemsPerPage: pg.itemsPerPage || prev.itemsPerPage,
          totalItems: pg.totalItems || personasData.length,
          totalPages: pg.totalPages || 1,
        }));
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load personas');
      } finally {
        setLoading(false);
      }
    };
    fetchPersonas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?._id, pagination.currentPage, pagination.itemsPerPage]);

  const getSegmentColor = (segment) => {
    switch (segment) {
      case 'High Priority':
        return 'error';
      case 'VIP':
        return 'warning';
      case 'Medium Priority':
        return 'primary';
      case 'Low Priority':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleDetailsOpen = (persona) => {
    setSelectedPersona(persona);
    setDetailsOpen(true);
  };

  const totalRevenue = personas.reduce((acc, persona) => acc + (Number(persona.revenue) || 0), 0);
  const totalCustomers = personas.reduce((acc, persona) => acc + (Number(persona.customerCount) || 0), 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Customer Persona Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ fontWeight: 'bold' }}
        >
          Create Persona
        </Button>
      </Box>

      {loading && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
        </Box>
      )}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Box>
      )}

      {/* Overview Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}>
                  {personas.length}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">Total Personas</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BusinessIcon color="info" />
                <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}>
                  {totalCustomers.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">Total Customers</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MoneyIcon color="success" />
                <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}>
                  ${totalRevenue.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon color="warning" />
                <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}>
                  ${Math.round(totalRevenue / totalCustomers)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">Avg Customer Value</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Personas Grid */}
      <Grid container spacing={3}>
        {personas.map((persona) => (
          <Grid item xs={12} md={6} lg={6} key={persona.id}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        mr: 2,
                        bgcolor: `${persona.color}.main`,
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                      }}
                    >
                      {(persona.name || 'P').slice(0, 2).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {persona.name}
                      </Typography>
                      <Chip
                        label={persona.segment}
                        color={getSegmentColor(persona.segment)}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Box>
                    <IconButton onClick={() => handleDetailsOpen(persona)} size="small">
                      <ViewIcon />
                    </IconButton>
                    <IconButton size="small">
                      <EditIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Demographics
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Age:</strong> {persona.demographics?.ageRange || '—'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Income:</strong> {persona.demographics?.income || '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Location:</strong> {persona.demographics?.location || '—'}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Key Interests
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(persona.psychographics?.interests || []).slice(0, 3).map((interest, index) => (
                      <Chip key={index} label={interest} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Performance Metrics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Customers</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {(Number(persona.customerCount) || 0).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Revenue</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        ${Number(persona.revenue || 0).toLocaleString()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Revenue Share
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={totalRevenue > 0 ? (Number(persona.revenue || 0) / totalRevenue) * 100 : 0}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {totalRevenue > 0 ? (((Number(persona.revenue || 0) / totalRevenue) * 100).toFixed(1)) : '0.0'}% of total revenue
                  </Typography>
                </Box>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => handleDetailsOpen(persona)}
                  sx={{ mt: 2 }}
                >
                  View Full Profile
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Persona Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                mr: 2,
                bgcolor: `${selectedPersona?.color}.main`,
                fontSize: '1.2rem',
                fontWeight: 'bold',
              }}
            >
              {selectedPersona?.avatar}
            </Avatar>
            {selectedPersona?.name} - Detailed Profile
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPersona && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Demographics
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon><PersonIcon /></ListItemIcon>
                    <ListItemText
                      primary="Age Range"
                      secondary={selectedPersona.demographics.ageRange}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><MoneyIcon /></ListItemIcon>
                    <ListItemText
                      primary="Income"
                      secondary={selectedPersona.demographics.income}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><LocationIcon /></ListItemIcon>
                    <ListItemText
                      primary="Location"
                      secondary={selectedPersona.demographics.location}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><SchoolIcon /></ListItemIcon>
                    <ListItemText
                      primary="Education"
                      secondary={selectedPersona.demographics.education}
                    />
                  </ListItem>
                </List>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Psychographics
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Values
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {selectedPersona.psychographics.values.map((value, index) => (
                      <Chip key={index} label={value} size="small" color="primary" />
                    ))}
                  </Box>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Interests
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {selectedPersona.psychographics.interests.map((interest, index) => (
                      <Chip key={index} label={interest} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Shopping Behavior
                  </Typography>
                  <Typography variant="body2">
                    {selectedPersona.psychographics.shoppingBehavior}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Pain Points
                </Typography>
                <List>
                  {selectedPersona.painPoints.map((point, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={`• ${point}`} />
                    </ListItem>
                  ))}
                </List>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Goals
                </Typography>
                <List>
                  {selectedPersona.goals.map((goal, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={`• ${goal}`} />
                    </ListItem>
                  ))}
                </List>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Marketing Approach
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedPersona.marketingApproach}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Preferred Channels
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                  {selectedPersona.preferredChannels.map((channel, index) => (
                    <Chip key={index} label={channel} size="small" color="info" />
                  ))}
                </Box>

                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Customer Journey
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">Awareness</Typography>
                    <Typography variant="body2">{selectedPersona.customerJourney.awareness}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">Consideration</Typography>
                    <Typography variant="body2">{selectedPersona.customerJourney.consideration}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">Purchase</Typography>
                    <Typography variant="body2">{selectedPersona.customerJourney.purchase}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">Retention</Typography>
                    <Typography variant="body2">{selectedPersona.customerJourney.retention}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button variant="contained">Edit Persona</Button>
        </DialogActions>
      </Dialog>

      {/* Create Persona Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Customer Persona</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Persona Name"
            margin="normal"
            placeholder="e.g., Tech-Savvy Millennial"
          />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Age Range"
                placeholder="e.g., 25-35"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Income Range"
                placeholder="e.g., $45,000 - $75,000"
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            label="Location"
            margin="normal"
            placeholder="e.g., Urban Areas"
          />
          <TextField
            fullWidth
            label="Key Interests"
            margin="normal"
            placeholder="Comma-separated interests"
          />
          <TextField
            fullWidth
            label="Pain Points"
            multiline
            rows={3}
            margin="normal"
            placeholder="List the main challenges this persona faces"
          />
          <TextField
            fullWidth
            label="Goals"
            multiline
            rows={3}
            margin="normal"
            placeholder="What does this persona want to achieve?"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained">Create Persona</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PersonaManagement;
